import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Form,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import TableContainer from '../../../../Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';
import classnames from 'classnames';

export interface AssociateOwnerData {
  id: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  userName: string;
  emailAddress: string;
  department: string;
  phoneNumber: string;
  active: boolean;
  manager: string;
}

export interface AssociateOwnerSearchForm {
  searchField: string;
  searchValue: string;
}

interface AssociateOwnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (associate: AssociateOwnerData) => void;
}

const defaultSearchForm: AssociateOwnerSearchForm = {
  searchField: 'Last Name',
  searchValue: ''
};

const mockAssociateOwners: AssociateOwnerData[] = [
  {
    id: '1',
    firstName: 'Sales',
    lastName: 'Our Business',
    jobTitle: 'Sales Manager',
    userName: 'sales.ourbusiness',
    emailAddress: 'sales@ourbusiness.com',
    department: 'Sales',
    phoneNumber: '+61 2 9876 5432',
    active: true,
    manager: 'John Smith'
  },
  {
    id: '2',
    firstName: 'Admin',
    lastName: 'System',
    jobTitle: 'System Administrator',
    userName: 'admin.system',
    emailAddress: 'admin@ourbusiness.com',
    department: 'IT',
    phoneNumber: '+61 2 9876 5433',
    active: true,
    manager: 'Jane Doe'
  },
  {
    id: '3',
    firstName: 'Support',
    lastName: 'Customer',
    jobTitle: 'Customer Support Lead',
    userName: 'support.customer',
    emailAddress: 'support@ourbusiness.com',
    department: 'Support',
    phoneNumber: '+61 2 9876 5434',
    active: true,
    manager: 'Mike Johnson'
  },
  {
    id: '4',
    firstName: 'Finance',
    lastName: 'Manager',
    jobTitle: 'Finance Manager',
    userName: 'finance.manager',
    emailAddress: 'finance@ourbusiness.com',
    department: 'Finance',
    phoneNumber: '+61 2 9876 5435',
    active: false,
    manager: 'Sarah Wilson'
  },
  {
    id: '5',
    firstName: 'Marketing',
    lastName: 'Director',
    jobTitle: 'Marketing Director',
    userName: 'marketing.director',
    emailAddress: 'marketing@ourbusiness.com',
    department: 'Marketing',
    phoneNumber: '+61 2 9876 5436',
    active: true,
    manager: 'David Brown'
  }
];

const searchFieldOptions = ['First Name', 'Last Name', 'Job Title', 'User Name', 'Email Address'];

const AssociateOwnerModal: React.FC<AssociateOwnerModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [searchForm, setSearchForm] = useState<AssociateOwnerSearchForm>(defaultSearchForm);
  const [associates, setAssociates] = useState<AssociateOwnerData[]>(mockAssociateOwners);
  const [selectedAssociate, setSelectedAssociate] = useState<AssociateOwnerData | null>(null);
  const [activeTab] = useState('1'); // Only one tab, but keep for extensibility

  const handleSearchFieldChange = (field: keyof AssociateOwnerSearchForm, value: string) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    const filtered = mockAssociateOwners.filter(associate => {
      let fieldValue = '';
      switch (searchForm.searchField) {
        case 'First Name':
          fieldValue = associate.firstName;
          break;
        case 'Last Name':
          fieldValue = associate.lastName;
          break;
        case 'Job Title':
          fieldValue = associate.jobTitle;
          break;
        case 'User Name':
          fieldValue = associate.userName;
          break;
        case 'Email Address':
          fieldValue = associate.emailAddress;
          break;
        default:
          fieldValue = associate.lastName;
      }
      return fieldValue.toLowerCase().includes(searchForm.searchValue.toLowerCase());
    });
    setAssociates(filtered);
  };

  const handleClear = () => {
    setSearchForm(defaultSearchForm);
    setAssociates(mockAssociateOwners);
    setSelectedAssociate(null);
  };

  const handleRowClick = (associate: AssociateOwnerData) => {
    setSelectedAssociate(associate);
  };

  const handleRowDoubleClick = (associate: AssociateOwnerData) => {
    if (onSelect) {
      onSelect(associate);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedAssociate(null);
    onClose();
  };

  const handleClears = () => {
    setSelectedAssociate(null);
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      header: 'First Name',
      accessorKey: 'firstName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAssociate?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Last Name',
      accessorKey: 'lastName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAssociate?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Job Title',
      accessorKey: 'jobTitle',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAssociate?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'User Name',
      accessorKey: 'userName',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAssociate?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    },
    {
      header: 'Email Address',
      accessorKey: 'emailAddress',
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div 
          onClick={() => handleRowClick(cell.row.original)}
          onDoubleClick={() => handleRowDoubleClick(cell.row.original)}
          style={{ cursor: 'pointer' }}
          className={selectedAssociate?.id === cell.row.original.id ? 'table-active' : ''}
        >
          {cell.getValue()}
        </div>
      )
    }
  ], [selectedAssociate]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Associate Owner</ModalHeader>
      <ModalBody>
        {/* Tab Navigation */}
        <Nav pills className="nav nav-tabs-custom arrow-navtabs nav-success mb-3">
          <NavItem>
            <NavLink
              style={{ cursor: 'pointer' }}
              className={classnames({ active: activeTab === '1' })}
              tabIndex={0}
              aria-label="Search Tab"
              onClick={() => {}}
              onKeyDown={() => {}}
            >
              Search
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab} className="text-muted">
          <TabPane tabId="1">
            <Form>
              <Row className="mb-2 justify-content-center align-items-center gap-2">
                <Col md={4}>
                  <SelectInput
                    formData={searchForm}
                    setFormData={setSearchForm}
                    label="Search"
                    id="searchField"
                    options={searchFieldOptions}
                  />
                </Col>
                <Col md={4}>
                  <FormInput
                    id="searchValue"
                    label="For value"
                    value={searchForm.searchValue}
                    onChange={(val) => handleSearchFieldChange('searchValue', val)}
                    type="text"
                  />
                </Col>
                <Col md={1} className="me-3">
                  <button
                    onClick={handleSearch}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-primary btn-sm"
                  >
                    Search
                  </button>
                </Col>
                <Col md={1}>
                  <button
                    onClick={handleClear}
                    style={{ minWidth: 80 }}
                    className="btn btn-soft-warning btn-sm"
                  >
                    Clear
                  </button>
                </Col>
              </Row>

              {/* Associate Owner Table */}
              <Row>
                <Col xs={12}>
                  <TableContainer
                    columns={columns}
                    data={associates || []}
                    isGlobalFilter={false}
                    customPageSize={10}
                    divClass="mb-0"
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="table-light"
                    thClass="px-3 py-2"
                  />
                </Col>
              </Row>
            </Form>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <button
          onClick={handleClears}
          className="btn btn-soft-warning btn-sm"
          style={{ minWidth: 80 }}
        >
          Clears
        </button>
        <button
          className="btn btn-soft-dark btn-sm"
          onClick={handleCancel}
          style={{ minWidth: 80 }}
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default AssociateOwnerModal; 